import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Landing from './pages/Landing';
import Home from './pages/Home';
import Chat from './pages/Chat';
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import Community from './pages/Community';
import Profile from './pages/Profile';
import Automations from './pages/Automations';
import Integrations from './pages/Integrations';
import Preferences from './pages/Preferences';
import Crew from './pages/Crew';
import Calendar from './pages/Calendar';
import CrewInvite from './pages/CrewInvite';
import ReferralJoin from './pages/ReferralJoin';
import Spots from './pages/Spots';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Tickets from './pages/Tickets';
import Garage from './pages/Garage';
import Trips from './pages/Trips';
import AdminLayout from './pages/admin/AdminLayout';
import TreasureChestPage from './pages/TreasureChestPage';
import Billing from './pages/Billing';
import OAuthCallback from './pages/OAuthCallback';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { authFetch, API } from './utils/api';


function ProtectedRoute({ children, user, loading }) {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative z-10">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      const storedToken = localStorage.getItem('swell_session_token');
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      const res = await authFetch(`${API}/api/auth/me`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
        localStorage.removeItem('swell_session_token');
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = useCallback((userData) => {
    if (userData) {
      setUser(userData);
      const path = window.location.pathname;
      if (path.startsWith('/join/')) {
        navigate(path, { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } else {
      setUser(null);
      setLoading(false);
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const headers = {};
      const storedToken = localStorage.getItem('swell_session_token');
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      await authFetch(`${API}/api/auth/logout`, { method: 'POST', headers });
    } catch {}
    localStorage.removeItem('swell_session_token');
    setUser(null);
    navigate('/');
  };

  return (
    <SubscriptionProvider user={user}>
    <Routes>
      <Route path="/" element={user ? <Navigate to="/home" replace /> : <Landing onLogin={handleLogin} />} />
      <Route path="/home" element={
        <ProtectedRoute user={user} loading={loading}>
          <Home user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute user={user} loading={loading}>
          <Chat user={user} />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute user={user} loading={loading}>
          <Dashboard user={user} />
        </ProtectedRoute>
      } />
      <Route path="/sessions" element={
        <ProtectedRoute user={user} loading={loading}>
          <Sessions user={user} />
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute user={user} loading={loading}>
          <Community user={user} />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute user={user} loading={loading}>
          <Profile user={user} setUser={setUser} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/swell-skills" element={
        <ProtectedRoute user={user} loading={loading}>
          <Automations user={user} />
        </ProtectedRoute>
      } />
      <Route path="/automations" element={<Navigate to="/swell-skills" replace />} />
      <Route path="/integrations" element={
        <ProtectedRoute user={user} loading={loading}>
          <Integrations user={user} />
        </ProtectedRoute>
      } />
      <Route path="/preferences" element={
        <ProtectedRoute user={user} loading={loading}>
          <Preferences user={user} />
        </ProtectedRoute>
      } />
      <Route path="/crew" element={
        <ProtectedRoute user={user} loading={loading}>
          <Crew user={user} />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute user={user} loading={loading}>
          <Calendar user={user} />
        </ProtectedRoute>
      } />
      <Route path="/spots" element={
        <ProtectedRoute user={user} loading={loading}>
          <Spots user={user} />
        </ProtectedRoute>
      } />
      <Route path="/join/:token" element={<CrewInvite user={user} />} />
      <Route path="/r/:code" element={<ReferralJoin user={user} />} />
      <Route path="/oauth-callback" element={<OAuthCallback onLogin={handleLogin} />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/security" element={<Security />} />
      <Route path="/tickets" element={
        <ProtectedRoute user={user} loading={loading}>
          <Tickets />
        </ProtectedRoute>
      } />
      <Route path="/garage" element={
        <ProtectedRoute user={user} loading={loading}>
          <Garage />
        </ProtectedRoute>
      } />
      <Route path="/trips" element={
        <ProtectedRoute user={user} loading={loading}>
          <Trips />
        </ProtectedRoute>
      } />
      <Route path="/treasure-chest" element={
        <ProtectedRoute user={user} loading={loading}>
          <TreasureChestPage />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute user={user} loading={loading}>
          <Billing />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute user={user} loading={loading}>
          {user?.is_admin ? <AdminLayout /> : <Navigate to="/home" replace />}
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </SubscriptionProvider>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AppBackground />
          <AppRouter />
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function AppBackground() {
  const { bgImage, isDark } = useTheme();
  return (
    <div
      className={`app-bg-layer ${isDark ? 'dark-overlay' : 'light-overlay'}`}
      style={{ backgroundImage: `url(${bgImage})` }}
      data-testid="app-background"
    />
  );
}
